import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Tooltip,
  Textarea,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiDatabase,
  FiClock,
  FiHardDrive,
  FiActivity,
  FiSearch,
  FiServer,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
  FiZap,
  FiTerminal,
  FiDownload,
} from 'react-icons/fi';

const API_BASE = '/api';

function PostgresStatus() {
  const toast = useToast();
  const [pgData, setPgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [tableSearch, setTableSearch] = useState('');
  const [tableSort, setTableSort] = useState('size');
  const [tableList, setTableList] = useState([]);
  const [tablePagination, setTablePagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [tablesLoading, setTablesLoading] = useState(false);

  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Query state
  const [querySql, setQuerySql] = useState('SELECT table_name, pg_size_pretty(pg_total_relation_size(quote_ident(table_name))) AS size\nFROM information_schema.tables\nWHERE table_schema = \'public\'\nORDER BY pg_total_relation_size(quote_ident(table_name)) DESC\nLIMIT 20;');
  const [queryResult, setQueryResult] = useState(null);
  const [queryRunning, setQueryRunning] = useState(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchStatus = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/postgres/status/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setPgData(data.postgres);
        if (showToast) {
          toast({ title: 'PostgreSQL status refreshed', status: 'success', duration: 2000 });
        }
      } else {
        toast({ title: 'Error loading PostgreSQL status', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error connecting to API', status: 'error', duration: 3000 });
    }

    setLoading(false);
    setRefreshing(false);
  }, [toast]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStatus(), 15000);
    return () => clearInterval(interval);
  }, [fetchStatus, autoRefresh]);

  const fetchTables = async (page = 1, sort = tableSort, search = tableSearch) => {
    setTablesLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), sort, search });
      const res = await fetch(`${API_BASE}/postgres/tables/?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setTableList(data.tables);
        setTablePagination(data.pagination);
      } else {
        toast({ title: data.message || 'Error listing tables', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error listing tables', status: 'error', duration: 3000 });
    }
    setTablesLoading(false);
  };

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const res = await fetch(`${API_BASE}/postgres/activity/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setActivity(data.activity);
      } else {
        toast({ title: data.message || 'Error loading activity', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error loading activity', status: 'error', duration: 3000 });
    }
    setActivityLoading(false);
  };

  const executeQuery = async () => {
    if (!querySql.trim()) return;
    setQueryRunning(true);
    setQueryResult(null);
    try {
      const res = await fetch(`${API_BASE}/postgres/query/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sql: querySql }),
      });
      const data = await res.json();
      setQueryResult(data);
      if (!data.success) {
        toast({ title: data.message || 'Query failed', status: 'error', duration: 5000 });
      }
    } catch (e) {
      toast({ title: 'Error executing query', status: 'error', duration: 3000 });
    }
    setQueryRunning(false);
  };

  const downloadCsv = () => {
    if (!queryResult?.rows || queryResult.rows.length === 0) return;
    const cols = queryResult.columns;
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.map(escapeCsv).join(',');
    const rows = queryResult.rows.map(row => cols.map(c => escapeCsv(row[c])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTableSearch = (e) => {
    e.preventDefault();
    fetchTables(1, tableSort, tableSearch);
  };

  const formatUptime = (isoString) => {
    if (!isoString) return 'N/A';
    const start = new Date(isoString);
    const now = new Date();
    const diffMs = now - start;
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatDuration = (secs) => {
    if (secs === null || secs === undefined) return '-';
    if (secs < 1) return '<1s';
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
  };

  const formatTimestamp = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const stateColor = (state) => {
    const colors = {
      active: 'green',
      idle: 'gray',
      'idle in transaction': 'yellow',
      'idle in transaction (aborted)': 'red',
      disabled: 'red',
    };
    return colors[state] || 'gray';
  };

  const connectionPercent = (conns) => {
    if (!conns || !conns.max_connections) return 0;
    return Math.round((conns.total / conns.max_connections) * 100);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="teal.500" />
        <Text mt={4}>Loading PostgreSQL status...</Text>
      </Box>
    );
  }

  const connected = pgData?.connected;
  const info = pgData?.server_info || {};
  const db = pgData?.database || {};
  const conns = pgData?.connections || {};
  const tables = pgData?.tables || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={3}>
          <Heading size="lg">PostgreSQL Status</Heading>
          <Badge
            colorScheme={connected ? 'green' : 'red'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {connected ? 'Connected' : 'Disconnected'}
          </Badge>
        </HStack>
        <HStack spacing={3}>
          <Button
            size="sm"
            variant={autoRefresh ? 'solid' : 'outline'}
            colorScheme="teal"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="teal"
            variant="outline"
            onClick={() => fetchStatus(true)}
            isLoading={refreshing}
            size="sm"
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      {pgData?.error && !connected && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">PostgreSQL Connection Error</Text>
            <Code mt={1} fontSize="sm">{pgData.error}</Code>
          </Box>
        </Alert>
      )}

      {!connected && (
        <Alert status="warning" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Backend: {pgData?.backend}</Text>
            <Text fontSize="sm">PostgreSQL is not connected or not in use.</Text>
          </Box>
        </Alert>
      )}

      {connected && (
        <Tabs colorScheme="teal" onChange={(idx) => {
          if (idx === 1 && tableList.length === 0) fetchTables();
          if (idx === 2) fetchActivity();
        }}>
          <TabList>
            <Tab><HStack spacing={2}><FiActivity /><Text>Overview</Text></HStack></Tab>
            <Tab><HStack spacing={2}><FiLayers /><Text>Tables</Text></HStack></Tab>
            <Tab><HStack spacing={2}><FiZap /><Text>Active Queries</Text></HStack></Tab>
            <Tab><HStack spacing={2}><FiTerminal /><Text>Query</Text></HStack></Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                  <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="teal.400">
                    <StatLabel color="gray.500">
                      <HStack spacing={1}><FiServer /><Text>Version</Text></HStack>
                    </StatLabel>
                    <StatNumber fontSize="xl" color="teal.600">PG {info.version?.split(' ')[0]}</StatNumber>
                    <StatHelpText>{info.host}:{info.port}</StatHelpText>
                  </Stat>

                  <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="green.400">
                    <StatLabel color="gray.500">
                      <HStack spacing={1}><FiClock /><Text>Uptime</Text></HStack>
                    </StatLabel>
                    <StatNumber fontSize="xl" color="green.600">{formatUptime(info.start_time)}</StatNumber>
                    <StatHelpText>Since {formatTimestamp(info.start_time)}</StatHelpText>
                  </Stat>

                  <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="purple.400">
                    <StatLabel color="gray.500">
                      <HStack spacing={1}><FiZap /><Text>Latency</Text></HStack>
                    </StatLabel>
                    <StatNumber fontSize="xl" color="purple.600">{info.latency_ms} ms</StatNumber>
                    <StatHelpText>Query round-trip</StatHelpText>
                  </Stat>

                  <Stat bg="white" p={5} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="orange.400">
                    <StatLabel color="gray.500">
                      <HStack spacing={1}><FiHardDrive /><Text>Database Size</Text></HStack>
                    </StatLabel>
                    <StatNumber fontSize="xl" color="orange.600">{db.size_pretty}</StatNumber>
                    <StatHelpText>{db.name}</StatHelpText>
                  </Stat>
                </SimpleGrid>

                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <HStack mb={4}>
                    <FiActivity />
                    <Heading size="md">Connections</Heading>
                    <Badge colorScheme={connectionPercent(conns) > 80 ? 'red' : connectionPercent(conns) > 50 ? 'yellow' : 'green'}>
                      {conns.total} / {conns.max_connections}
                    </Badge>
                  </HStack>
                  <Box mb={4}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">Connection Usage</Text>
                      <Text fontSize="sm" color="gray.500">{connectionPercent(conns)}%</Text>
                    </HStack>
                    <Progress
                      value={connectionPercent(conns)}
                      colorScheme={connectionPercent(conns) > 80 ? 'red' : connectionPercent(conns) > 50 ? 'yellow' : 'green'}
                      borderRadius="full"
                      size="sm"
                    />
                  </Box>
                  <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
                    <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.500" mb={1}>Total</Text>
                      <Text fontSize="2xl" fontWeight="bold">{conns.total}</Text>
                    </Box>
                    <Box p={4} bg="green.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.500" mb={1}>Active</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.600">{conns.active}</Text>
                    </Box>
                    <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.500" mb={1}>Idle</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.600">{conns.idle}</Text>
                    </Box>
                    <Box p={4} bg="yellow.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.500" mb={1}>Idle in Txn</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="yellow.600">{conns.idle_in_transaction}</Text>
                    </Box>
                    <Box p={4} bg="red.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" color="gray.500" mb={1}>Waiting</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="red.600">{conns.waiting}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <Heading size="md" mb={4}>Server Configuration</Heading>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Setting</Th>
                        <Th>Value</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td>Max Connections</Td>
                        <Td><Code>{info.max_connections}</Code></Td>
                      </Tr>
                      <Tr>
                        <Td>Shared Buffers</Td>
                        <Td><Code>{info.shared_buffers}</Code></Td>
                      </Tr>
                      <Tr>
                        <Td>Work Memory</Td>
                        <Td><Code>{info.work_mem}</Code></Td>
                      </Tr>
                      <Tr>
                        <Td>Effective Cache Size</Td>
                        <Td><Code>{info.effective_cache_size}</Code></Td>
                      </Tr>
                      <Tr>
                        <Td>Database</Td>
                        <Td><Code>{db.name}</Code></Td>
                      </Tr>
                      <Tr>
                        <Td>Total Tables</Td>
                        <Td><Badge colorScheme="teal">{tables.total_tables}</Badge></Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <Heading size="md" mb={4}>Database Tables</Heading>
                  <form onSubmit={handleTableSearch}>
                    <HStack mb={4}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none"><FiSearch color="gray" /></InputLeftElement>
                        <Input
                          placeholder="Filter tables by name..."
                          value={tableSearch}
                          onChange={(e) => setTableSearch(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </InputGroup>
                      <HStack spacing={1}>
                        {[
                          { val: 'size', label: 'Size' },
                          { val: 'rows', label: 'Rows' },
                          { val: 'name', label: 'Name' },
                        ].map(s => (
                          <Button
                            key={s.val}
                            size="sm"
                            variant={tableSort === s.val ? 'solid' : 'outline'}
                            colorScheme={tableSort === s.val ? 'teal' : 'gray'}
                            onClick={() => { setTableSort(s.val); fetchTables(1, s.val, tableSearch); }}
                          >
                            {s.label}
                          </Button>
                        ))}
                      </HStack>
                      <Button type="submit" colorScheme="teal" isLoading={tablesLoading} px={8}>
                        Search
                      </Button>
                    </HStack>
                  </form>
                </Box>

                {tablesLoading ? (
                  <Box textAlign="center" py={10}>
                    <Spinner size="lg" color="teal.500" />
                    <Text mt={2}>Loading tables...</Text>
                  </Box>
                ) : tableList.length > 0 ? (
                  <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                    <HStack justify="space-between" p={4} bg="gray.50" borderBottom="1px" borderColor="gray.200">
                      <Text fontSize="sm" color="gray.600">
                        {tablePagination.total} table{tablePagination.total !== 1 ? 's' : ''} found
                      </Text>
                      {tablePagination.total_pages > 1 && (
                        <HStack spacing={2}>
                          <IconButton
                            icon={<FiChevronLeft />}
                            size="sm"
                            variant="outline"
                            isDisabled={tablePagination.page <= 1}
                            onClick={() => fetchTables(tablePagination.page - 1)}
                            aria-label="Previous page"
                          />
                          <Text fontSize="sm" color="gray.600">
                            Page {tablePagination.page} / {tablePagination.total_pages}
                          </Text>
                          <IconButton
                            icon={<FiChevronRight />}
                            size="sm"
                            variant="outline"
                            isDisabled={tablePagination.page >= tablePagination.total_pages}
                            onClick={() => fetchTables(tablePagination.page + 1)}
                            aria-label="Next page"
                          />
                        </HStack>
                      )}
                    </HStack>
                    <Box overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Table Name</Th>
                            <Th isNumeric>Rows (est.)</Th>
                            <Th>Total Size</Th>
                            <Th>Data</Th>
                            <Th>Indexes</Th>
                            <Th isNumeric>Dead Tuples</Th>
                            <Th isNumeric>Seq Scans</Th>
                            <Th isNumeric>Idx Scans</Th>
                            <Th>Last Vacuum</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {tableList.map((t) => (
                            <Tr key={t.table_name} _hover={{ bg: 'gray.50' }}>
                              <Td>
                                <Code fontSize="xs">{t.table_name}</Code>
                              </Td>
                              <Td isNumeric>
                                <Text fontWeight="medium">{(t.row_estimate || 0).toLocaleString()}</Text>
                              </Td>
                              <Td>
                                <Badge colorScheme="teal" fontSize="xs">{t.size_pretty}</Badge>
                              </Td>
                              <Td>
                                <Text fontSize="xs" color="gray.500">{t.data_size}</Text>
                              </Td>
                              <Td>
                                <Text fontSize="xs" color="gray.500">{t.index_size}</Text>
                              </Td>
                              <Td isNumeric>
                                {(t.dead_tuples || 0) > 1000 ? (
                                  <Badge colorScheme="red" fontSize="xs">{(t.dead_tuples || 0).toLocaleString()}</Badge>
                                ) : (
                                  <Text fontSize="xs" color="gray.500">{(t.dead_tuples || 0).toLocaleString()}</Text>
                                )}
                              </Td>
                              <Td isNumeric>
                                <Text fontSize="xs" color="gray.500">{(t.seq_scan || 0).toLocaleString()}</Text>
                              </Td>
                              <Td isNumeric>
                                <Text fontSize="xs" color="gray.500">{(t.idx_scan || 0).toLocaleString()}</Text>
                              </Td>
                              <Td>
                                <Tooltip label={formatTimestamp(t.last_vacuum)}>
                                  <Text fontSize="xs" color="gray.500">
                                    {t.last_vacuum ? new Date(t.last_vacuum).toLocaleDateString() : 'Never'}
                                  </Text>
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                ) : (
                  <Box bg="white" p={8} borderRadius="lg" boxShadow="sm" textAlign="center">
                    <Text color="gray.500">No tables found</Text>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md">Active Queries & Connections</Heading>
                  <Button
                    leftIcon={<FiRefreshCw />}
                    size="sm"
                    colorScheme="teal"
                    variant="outline"
                    onClick={fetchActivity}
                    isLoading={activityLoading}
                  >
                    Refresh
                  </Button>
                </HStack>

                {activityLoading ? (
                  <Box textAlign="center" py={10}>
                    <Spinner size="lg" color="teal.500" />
                    <Text mt={2}>Loading activity...</Text>
                  </Box>
                ) : activity.length > 0 ? (
                  <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                    <Box overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th width="70px">PID</Th>
                            <Th width="100px">User</Th>
                            <Th width="100px">State</Th>
                            <Th width="90px">Duration</Th>
                            <Th>Wait</Th>
                            <Th>Query</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {activity.map((a) => (
                            <Tr key={a.pid} _hover={{ bg: 'gray.50' }}>
                              <Td>
                                <Code fontSize="xs">{a.pid}</Code>
                              </Td>
                              <Td>
                                <Text fontSize="xs">{a.usename || '-'}</Text>
                              </Td>
                              <Td>
                                <Badge colorScheme={stateColor(a.state)} fontSize="xs">
                                  {a.state || 'unknown'}
                                </Badge>
                              </Td>
                              <Td>
                                <Text fontSize="xs" color={a.query_duration_secs > 30 ? 'red.500' : 'gray.600'} fontWeight={a.query_duration_secs > 30 ? 'bold' : 'normal'}>
                                  {formatDuration(a.query_duration_secs)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="xs" color="gray.500">
                                  {a.wait_event_type ? `${a.wait_event_type}/${a.wait_event}` : '-'}
                                </Text>
                              </Td>
                              <Td maxW="500px">
                                <Tooltip label={a.query} placement="top-start" hasArrow>
                                  <Code fontSize="xs" noOfLines={2} display="block" whiteSpace="pre-wrap" wordBreak="break-all">
                                    {a.query || '-'}
                                  </Code>
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box p={3} bg="gray.50" borderTop="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500">
                        {activity.length} connection{activity.length !== 1 ? 's' : ''} shown (excluding this session)
                      </Text>
                    </Box>
                  </Box>
                ) : (
                  <Box bg="white" p={8} borderRadius="lg" boxShadow="sm" textAlign="center">
                    <FiDatabase size={32} style={{ margin: '0 auto', color: '#A0AEC0' }} />
                    <Text color="gray.500" mt={3}>No active connections (other than this session)</Text>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            {/* Query Tab */}
            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <HStack spacing={2} mb={4}>
                    <FiTerminal />
                    <Heading size="md">SQL Query</Heading>
                    <Badge colorScheme="yellow" fontSize="xs">Read-only</Badge>
                  </HStack>
                  <Textarea
                    value={querySql}
                    onChange={(e) => setQuerySql(e.target.value)}
                    placeholder="SELECT * FROM ..."
                    fontFamily="mono"
                    fontSize="sm"
                    minH="150px"
                    bg="gray.900"
                    color="green.300"
                    border="none"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{ border: 'none', boxShadow: 'none' }}
                    mb={3}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                        e.preventDefault();
                        executeQuery();
                      }
                    }}
                  />
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">⌘+Enter to execute. Write operations (DROP, DELETE, INSERT, UPDATE, etc.) are blocked.</Text>
                    <HStack spacing={2}>
                      {queryResult?.rows && queryResult.rows.length > 0 && (
                        <Button
                          leftIcon={<FiDownload />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={downloadCsv}
                        >
                          Download CSV
                        </Button>
                      )}
                      <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={executeQuery}
                        isLoading={queryRunning}
                        loadingText="Running..."
                      >
                        Execute
                      </Button>
                    </HStack>
                  </HStack>
                </Box>

                {queryResult && (
                  <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                    <HStack p={4} bg={queryResult.success ? 'green.50' : 'red.50'} borderBottom="1px" borderColor="gray.200" justify="space-between">
                      <HStack spacing={3}>
                        <Badge colorScheme={queryResult.success ? 'green' : 'red'}>
                          {queryResult.success ? 'Success' : 'Error'}
                        </Badge>
                        {queryResult.success && (
                          <>
                            <Text fontSize="sm" color="gray.600">{queryResult.row_count} row{queryResult.row_count !== 1 ? 's' : ''}</Text>
                            <Text fontSize="sm" color="gray.500">•</Text>
                            <Text fontSize="sm" color="gray.600">{queryResult.duration_ms} ms</Text>
                          </>
                        )}
                      </HStack>
                      {queryResult.rows && queryResult.rows.length > 0 && (
                        <Button leftIcon={<FiDownload />} size="xs" colorScheme="green" variant="ghost" onClick={downloadCsv}>
                          CSV
                        </Button>
                      )}
                    </HStack>

                    {!queryResult.success ? (
                      <Box p={4}>
                        <Code colorScheme="red" fontSize="sm" whiteSpace="pre-wrap">{queryResult.message}</Code>
                      </Box>
                    ) : queryResult.columns && queryResult.columns.length > 0 ? (
                      <Box overflowX="auto" maxH="500px" overflowY="auto">
                        <Table size="sm">
                          <Thead position="sticky" top={0} bg="white" zIndex={1}>
                            <Tr>
                              {queryResult.columns.map((col, i) => (
                                <Th key={i} fontSize="xs">{col}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {queryResult.rows.map((row, ri) => (
                              <Tr key={ri} _hover={{ bg: 'gray.50' }}>
                                {queryResult.columns.map((col, ci) => (
                                  <Td key={ci} fontSize="xs" maxW="300px">
                                    <Tooltip label={String(row[col] ?? '')} placement="top-start">
                                      <Text isTruncated>{row[col] === null ? <Text as="span" color="gray.400" fontStyle="italic">NULL</Text> : String(row[col])}</Text>
                                    </Tooltip>
                                  </Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Box p={4} textAlign="center">
                        <Text color="gray.500" fontSize="sm">Query executed successfully (no rows returned)</Text>
                      </Box>
                    )}
                  </Box>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}

export default PostgresStatus;
